import { useContext, useState } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import JSZip from "jszip";
import { saveAs } from 'file-saver';

import { ReactComponent as IconMinimize } from 'assets/images/minimize.svg';
import iconDonwloadSvg from 'assets/images/icon-download.svg';
import { RulesTasksCards } from "../RulesTask.constanst";
import BorrowerTask, { ExpandIcon } from "components/BorrowerTask/BorrowerTasks";
import { StatusBadge } from "../../LoanList/LoanList";
import { Task, TaskState } from "../../../../gql/graphql";
import { useDownloadFilePending } from "../../../../services/loan/querys";
import { LoanContext, TasksContext } from "../LoanDetails";
import AddNewTask from "../AddNewTask/AddNewTask";
import { sanitizeFileName } from "../../../../@common/utils/helpers/sanitize-doc-name";

interface IBorrowerTasksProps {
	// Define your props here
}

export const BorrowerTasks: React.FC<IBorrowerTasksProps> = (props) => {

	const tasks: Task[] = useContext(TasksContext);
	const loan: any = useContext(LoanContext);

	const { data: filesDownload } = useDownloadFilePending({
		variables: { loanId: loan._id }
	});

	const [filterSelected, setFilterSelected] = useState<string>('all');
	const [loadingDonwload, setLoadingDownload] = useState<boolean>(false);
	const [expandAll, setExpandAll] = useState<boolean>(false);
	
  const createAndDownloadZip = async (taskId?: string, zipname?: string) => {
    if (!taskId)
      setLoadingDownload(true);

    const zip = new JSZip();
    const token = localStorage.getItem('token');
    const options: any = {
      mode: 'cors',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }

    if (filesDownload) {
      const remoteFiles = filesDownload?.map(async (file, index) => {
        if (!taskId || taskId === file.task._id) {
          if (!file?.file?.url || !file?.file?.name) return null;
          try {
            const response = await fetch(file.file.url, options);
            const data = await response.blob();
            const fileName = `${index + 1}-${sanitizeFileName(file.file.name)}`;
            zip.file(fileName, data);
            return data;
          } catch (error) {
            console.error(`Failed to fetch ${file.file.url}`, error);
            return null;
          }
        }
        return 'SKIP';
      });

      const fetchedFiles = await Promise.all(remoteFiles);
      const attemptedFiles = fetchedFiles.filter(f => f !== 'SKIP');
      const successfulFiles = attemptedFiles.filter(f => f !== null);

      if (attemptedFiles.length > 0 && successfulFiles.length === attemptedFiles.length) {
        zip.generateAsync({ type: 'blob' }).then((content) => {
          saveAs(content, zipname || 'files-in-approved.zip');
          setLoadingDownload(false);
        });
      } else {
        console.error(`Failed to download ${attemptedFiles.length - successfulFiles.length} of ${attemptedFiles.length} files`);
        setLoadingDownload(false);
      }
    }
  };

	const downloadAll = () => createAndDownloadZip();

	const downloadFilesToTask = (taskId: string, taskName: string) => {
		createAndDownloadZip(taskId, `files-${taskName}.zip`);
	}
	
	const hasFilesPending = (taskId: string) => {
		return filesDownload?.some(file => file.task._id === taskId) ?? false;
	}

	const newTasks = tasks?.filter((item) => !item.key && !item.hidden);
	const getCountState = (state: TaskState) => tasks?.filter(task =>
		task.state === state && !task.hidden)?.length;

	const filters = [
		{ label: 'All', key: 'all', count: tasks?.filter(task => !task.hidden)?.length },
		{ label: 'In Progress', key: TaskState.Incomplete, count: getCountState(TaskState.Incomplete) },
		{ label: 'Pending Review', key: TaskState.Review, count: getCountState(TaskState.Review) },
		{ label: 'Rejected', key: TaskState.RequestError, count: getCountState(TaskState.RequestError) },
		{ label: 'Approved', key: TaskState.Approved, count: getCountState(TaskState.Approved) },
	];

	const TaskFilter = (loanSelected: any, tasks: any) => {
		const { type, purpose } = loanSelected;

		if (!tasks) return [];

		const filteredTasks = tasks.filter((task: any) => {
			if (purpose !== "Purchase" && task.key === "purchase") return false;
			if (type !== "Rental" && task.key === "dscr") return false;
			if (type === "Rental" && (task.key === "track_record" || task.key === "investment_experience" || task.key === "construction_budget")) return false;
			return true;
		})

		return filteredTasks;
	}

	//@ts-ignore
	const tasksAdmin = TaskFilter(loan, tasks)?.filter(item => !!item.key && !item.hidden);
	// @ts-ignore
	tasksAdmin?.sort((a, b) => {
		const indexA = RulesTasksCards.findIndex(item => item.key === a.key);
		const indexB = RulesTasksCards.findIndex(item => item.key === b.key);
		return indexA - indexB;
	});

	return (
		<div className='borrower-tasks'>
			<div className='title'>
				Borrower Tasks
			</div>
			<AddNewTask />
			<div className='borrower-tasks-status-container'>
				{filters.map(filter => (
					<StatusBadge
						key={filter.key}
						title={filter.label}
						count={filter.count}
						isSelected={filterSelected === filter.key}
						onClick={() => setFilterSelected(filter.key)}
					/>
				))}
			</div>
			<div className='borrower-tasks-actions'>
				{/*<div><EyeIcon /> VIEW DOC LIST</div>*/}
				<div onClick={downloadAll}>
					{loadingDonwload && <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />}
					{!loadingDonwload && <img alt="download" src={iconDonwloadSvg} />}
					DOWNLOAD ALL
				</div>
				<div onClick={() => setExpandAll(!expandAll)}>
					{expandAll &&
						<>
							<IconMinimize />
							CLOSE EXPAND ALL
						</>
					}
					{!expandAll &&
						<>
							<ExpandIcon />
							EXPAND ALL
						</>
					}
				</div>
			</div>
			<div className='borrower-tasks-list'>
				{tasksAdmin?.map((task: any) => task.key && (
					<BorrowerTask
						key={task._id}
						taskKey={task.key}
						label={task.name}
						filterSelected={filterSelected}
						onClickDownloadFiles={hasFilesPending(task._id) ? downloadFilesToTask : undefined}
						expandAll={expandAll}
					/>
				))}
				{newTasks?.map((item, index) => (
					<BorrowerTask
						key={item._id || index}
						taskKey={item._id}
						label={item.name}
						filterSelected={filterSelected}
						onClickDownloadFiles={hasFilesPending(item._id) ? downloadFilesToTask : undefined}
						expandAll={expandAll}
						isTaskNew />
				))}
			</div>
		</div>
	);
};